<template>
  <div class="lp-card">
    <div class="lp-card-head">
      <div class="lp-card-title-block">
        <div class="lp-card-kicker">
          Personnalisation
        </div>
        <h3 class="lp-card-title">
          Ton mode d'apprentissage
        </h3>
      </div>
      <button
        type="button"
        class="lp-btn ghost"
        @click="$emit('edit')"
      >
        Ajuster
      </button>
    </div>

    <div
      v-if="!profile || !profile.onboardingCompleted"
      class="lp-card-empty"
    >
      <p>
        On n'a pas encore ton profil. Prends 30 secondes pour que Nick te propose un parcours vraiment adapté.
      </p>
      <button
        type="button"
        class="lp-btn primary"
        @click="$emit('onboard')"
      >
        Configurer en 30 secondes
      </button>
    </div>

    <template v-else>
      <div class="lp-grid">
        <div class="lp-tile">
          <div class="lp-tile-label">
            Mode
          </div>
          <div
            class="lp-tile-value"
            :class="'mode-' + profile.learningMode"
          >
            {{ modeLabel }}
          </div>
          <div class="lp-tile-help">
            {{ modeHelp }}
          </div>
        </div>
        <div class="lp-tile">
          <div class="lp-tile-label">
            Format préféré
          </div>
          <div class="lp-tile-value">
            {{ explanationLabel }}
          </div>
          <div class="lp-tile-help">
            {{ explanationHelp }}
          </div>
        </div>
        <div class="lp-tile">
          <div class="lp-tile-label">
            Ton de Nick
          </div>
          <div class="lp-tile-value">
            {{ toneLabel }}
          </div>
          <div class="lp-tile-help">
            {{ toneHelp }}
          </div>
        </div>
      </div>

      <div class="lp-row">
        <div class="lp-meter">
          <div class="lp-meter-label">
            <span>Confiance</span>
            <span class="lp-meter-val">{{ Math.round(confidenceLevel) }}%</span>
          </div>
          <div class="lp-meter-bar">
            <div
              class="lp-meter-fill"
              :style="{ width: confidenceLevel + '%' }"
            />
          </div>
        </div>
        <div class="lp-meter">
          <div class="lp-meter-label">
            <span>Précision moyenne</span>
            <span class="lp-meter-val">{{ accuracy }}%</span>
          </div>
          <div class="lp-meter-bar">
            <div
              class="lp-meter-fill accent"
              :style="{ width: accuracy + '%' }"
            />
          </div>
        </div>
      </div>

      <div
        v-if="weakAreas.length > 0"
        class="lp-chips"
      >
        <span class="lp-chips-label">Fragile :</span>
        <span
          v-for="w in weakAreas"
          :key="'weak-' + w"
          class="lp-chip weak"
        >{{ w }}</span>
      </div>
      <div
        v-if="strongAreas.length > 0"
        class="lp-chips"
      >
        <span class="lp-chips-label">Maîtrisé :</span>
        <span
          v-for="s in strongAreas"
          :key="'strong-' + s"
          class="lp-chip strong"
        >{{ s }}</span>
      </div>

      <div
        v-if="lastAdjustment"
        class="lp-adjustment"
      >
        <span class="lp-adjustment-dot" />
        <span>
          Nick a ajusté <strong>{{ lastAdjustment.field }}</strong>
          ({{ lastAdjustment.from || '—' }} → {{ lastAdjustment.to || '—' }})
          : {{ lastAdjustment.reason }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'LearningProfileCard',
  props: {
    profile: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    confidenceLevel() {
      const b = (this.profile && this.profile.behavior) || {}
      return typeof b.confidenceLevel === 'number' ? b.confidenceLevel : 50
    },
    accuracy() {
      const b = (this.profile && this.profile.behavior) || {}
      return typeof b.avgAccuracy === 'number' ? Math.round(b.avgAccuracy) : 0
    },
    weakAreas() {
      return (this.profile && Array.isArray(this.profile.weakAreas)) ? this.profile.weakAreas.slice(0, 3) : []
    },
    strongAreas() {
      return (this.profile && Array.isArray(this.profile.strongAreas)) ? this.profile.strongAreas.slice(0, 3) : []
    },
    modeLabel() {
      return {
        coached: 'Coaché',
        balanced: 'Équilibré',
        exam: 'Examen',
      }[this.profile.learningMode] || 'Équilibré'
    },
    modeHelp() {
      return {
        coached: 'Plus d\'exemples et de checkpoints',
        balanced: 'Mix explications et exercices',
        exam: 'Format dense, proche de l\'examen',
      }[this.profile.learningMode] || ''
    },
    explanationLabel() {
      return {
        example_first: 'Exemple d\'abord',
        formula_first: 'Formule d\'abord',
        step_by_step: 'Pas à pas',
      }[this.profile.explanationStyle] || 'Pas à pas'
    },
    explanationHelp() {
      return {
        example_first: 'Un cas concret avant la règle',
        formula_first: 'La règle, puis les exemples',
        step_by_step: 'On déroule étape par étape',
      }[this.profile.explanationStyle] || ''
    },
    toneLabel() {
      return {
        reassuring: 'Rassurant',
        neutral: 'Neutre',
        challenging: 'Exigeant',
      }[this.profile.tone] || 'Neutre'
    },
    toneHelp() {
      return {
        reassuring: 'Phrases courtes, encourageantes',
        neutral: 'Factuel, direct',
        challenging: 'Pousse à aller plus loin',
      }[this.profile.tone] || ''
    },
    lastAdjustment() {
      const adjustments = (this.profile && Array.isArray(this.profile.autoAdjustments))
        ? this.profile.autoAdjustments
        : []
      return adjustments.length > 0 ? adjustments[adjustments.length - 1] : null
    },
  },
}
</script>

<style scoped>
.lp-card {
  background: #fff;
  border: 1px solid #e8e6f4;
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: 0 4px 18px rgba(60, 40, 150, 0.05);
}
.lp-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}
.lp-card-kicker {
  color: #7a6ed8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.lp-card-title {
  margin: 4px 0 0;
  font-size: 19px;
  color: #26254a;
  font-weight: 700;
}
.lp-card-empty {
  padding: 14px 0 0;
}
.lp-card-empty p {
  color: #5c5a82;
  margin: 0 0 12px;
  font-size: 14px;
}
.lp-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.lp-tile {
  background: #f7f5ff;
  border: 1px solid #eae6fb;
  border-radius: 12px;
  padding: 12px 14px;
}
.lp-tile-label {
  color: #7a6ed8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.lp-tile-value {
  color: #26254a;
  font-weight: 700;
  font-size: 16px;
}
.lp-tile-value.mode-coached { color: #0d896e; }
.lp-tile-value.mode-exam { color: #b8381f; }
.lp-tile-value.mode-balanced { color: #2a2a88; }
.lp-tile-help {
  color: #6b6996;
  font-size: 12px;
  margin-top: 4px;
}
.lp-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 12px;
}
.lp-meter-label {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #5c5a82;
  margin-bottom: 4px;
}
.lp-meter-val { color: #26254a; font-weight: 700; }
.lp-meter-bar {
  height: 6px;
  background: #ece9fb;
  border-radius: 10px;
  overflow: hidden;
}
.lp-meter-fill {
  height: 100%;
  background: linear-gradient(90deg, #7a6ed8, #5f5bd6);
  border-radius: 10px;
}
.lp-meter-fill.accent {
  background: linear-gradient(90deg, #0d896e, #26b77a);
}
.lp-chips {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.lp-chips-label {
  font-size: 12px;
  color: #6b6996;
  font-weight: 600;
}
.lp-chip {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 600;
}
.lp-chip.weak {
  background: #fdebe2;
  color: #a6441f;
}
.lp-chip.strong {
  background: #e7f6ed;
  color: #0d7a5d;
}
.lp-btn {
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.15s ease;
}
.lp-btn.primary { background: #5f5bd6; color: #fff; }
.lp-btn.primary:hover { background: #4a46c0; }
.lp-btn.ghost {
  background: transparent;
  color: #5f5bd6;
  border: 1px solid #d9d5f0;
}
.lp-btn.ghost:hover { background: #f7f5ff; }
.lp-adjustment {
  margin-top: 10px;
  padding: 10px 12px;
  background: #fefbe9;
  border-radius: 10px;
  font-size: 12px;
  color: #7a6400;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.lp-adjustment-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f1b400;
  margin-top: 4px;
  flex-shrink: 0;
}
@media (max-width: 700px) {
  .lp-grid { grid-template-columns: 1fr; }
  .lp-row { grid-template-columns: 1fr; }
}
</style>
